import { Component, OnDestroy, OnInit, ViewEncapsulation, inject, viewChild, input, effect, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { EMPTY, Observable, Subject } from 'rxjs';
import { filter, map, repeat, share, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ApContentService, ApContentPipe } from '@ap/content';
import {
    ApAmountValidator,
    ApBytesPipe,
    ApExtensionValidator,
    ApFileSelectorComponent,
    ApSizeValidator,
    FILE_VALIDATORS,
    ReadFile,
    UploadStatus as FileUploadStatus
} from '@ap/file';
import { ApUploadbeheerResource, Opdracht, OpdrachtStatus, UploadStatus } from '@ap/interface-apic-uploadbeheer';
import { ApSpinnerComponent } from '@ap/spinner';
import { ApIconComponent } from '@ap/icon';
import { ApButtonComponent } from '@ap/button';

import { ApUploadReadFile, UploadDocumentState, OpdrachtStatusEvent } from './file-upload.model';
import { ApFileUploadService } from './file-upload.service';
import { ApCombinedSizeValidator } from './validators/combined-size.validator';
import { ApFinishedValidator } from './validators/finished.validator';
import content from './file-upload.content.json';

@Component({
    selector: 'ap-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        ApBytesPipe,
        ApFinishedValidator,
        ApFileUploadService,
        ApUploadbeheerResource,
        { provide: FILE_VALIDATORS, useClass: ApCombinedSizeValidator, multi: true },
        { provide: FILE_VALIDATORS, useClass: ApAmountValidator, multi: true },
        { provide: FILE_VALIDATORS, useClass: ApExtensionValidator, multi: true },
        { provide: FILE_VALIDATORS, useClass: ApSizeValidator, multi: true },
        { provide: FILE_VALIDATORS, useExisting: ApFinishedValidator, multi: true }
    ],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApFileSelectorComponent, ApSpinnerComponent, ApIconComponent, ApButtonComponent, ApContentPipe]
})
export class ApFileUploadComponent implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);
    private readonly uploadbeheerResource = inject(ApUploadbeheerResource);
    private readonly fileUploadService = inject(ApFileUploadService);
    private readonly finishedValidator = inject(ApFinishedValidator);

    /**
     * Id of opdracht to upload
     */
    public readonly opdrachtId = input<string>(undefined);

    /**
     * Required - The FormControl for the input field
     */
    public readonly control = input.required<FormControl<ReadFile[]>>();

    /**
     * Upload subject
     */
    public readonly uploadSubject = input<Subject<void>>(undefined);

    /**
     * Whether to hide the upload button
     * Defaults to false
     */
    public readonly hideUploadButton = input(false);

    /**
     * Whether to hide the restart upload button
     * Defaults to false
     */
    public readonly hideRestartButton = input(false);

    /**
     * Compact form
     * Defaults to true
     */
    public readonly compact = input(true);

    /**
     * Maximum amount of files allowed
     * Defaults to 50
     */
    public readonly maxFilesAllowed = input(50);

    /**
     * Show upload preview
     * Defaults to false
     */
    public readonly uploadPreview = input(false);

    /**
     * EventEmitter emits boolean when upload is complete
     */
    public readonly uploadComplete = output<boolean>();

    /**
     * Eventemitter is emitted when upload is retried
     * */
    public readonly retryUpload = output<void>();
    /**
     * Eventemitter is emitted when upload status changes
     * */
    public readonly uploadStatus = output<OpdrachtStatusEvent>();
    protected readonly fileSelector = viewChild(ApFileSelectorComponent);

    protected processing = false;
    protected uploadFinished = false;
    protected document: UploadDocumentState;
    protected destroy$ = new Subject<void>();
    protected hidePreview = false;

    protected get showSaveButton(): boolean {
        return !this.uploadFinished && !this.hideUploadButton();
    }

    protected get showRestartButton(): boolean {
        return this.uploadFinished && !this.hideRestartButton();
    }

    constructor() {
        effect(() => {
            const opdrachtId = this.opdrachtId();

            if (opdrachtId) {
                this.getOpdracht(opdrachtId);
            }
        });
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        const uploadSubject = this.uploadSubject();
        if (uploadSubject) {
            uploadSubject.pipe(takeUntil(this.destroy$)).subscribe(() => this.saveFiles(this.document));
        }
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected selectFile(files: ApUploadReadFile[], state: UploadDocumentState): void {
        if (files === state.selectedFiles) {
            return;
        }
        if (files.length === 0 || files.find((file) => file.status === FileUploadStatus.DONE)) {
            return;
        }

        this.hidePreview = false;
        state.selectedFiles = files;
    }

    protected removeFile(file: ApUploadReadFile, state: UploadDocumentState): void {
        state.selectedFiles.splice(state.selectedFiles.indexOf(file), 1);
    }

    protected saveFiles(state: UploadDocumentState): Observable<boolean> {
        this.control().markAsTouched();
        this.control().markAsDirty();
        this.control().updateValueAndValidity();
        this.hidePreview = true;

        if (state.selectedFiles.length > 0) {
            const destroySubject = new Subject<void>();
            const upload = this.fileUploadService.uploadSingleFile(state.originalDocument.document_id, state.selectedFiles).pipe(share());

            const processing = upload.pipe(
                filter((response) => {
                    if (!response) {
                        throw new Error('Upload mislukt.');
                    }
                    return true;
                }),
                tap(() => (this.processing = true)),
                switchMap(() => this.uploadbeheerResource.getOpdracht(this.opdrachtId())),
                map((opdracht) => {
                    if (opdracht.opdracht_status) {
                        this.uploadStatus.emit({
                            status: opdracht.opdracht_status,
                            processing: !this.isUploadFinished(opdracht) && !this.isActionableDocumentState(this.document)
                        });
                    }
                    if (
                        opdracht.documenten.some(
                            (document) =>
                                document.upload_status === UploadStatus.MISLUKT || document.upload_status === UploadStatus.AFGEKEURD
                        )
                    ) {
                        throw new Error('Document uploaden mislukt.');
                    } else {
                        return opdracht;
                    }
                }),
                filter((opdracht) => opdracht.opdracht_status === OpdrachtStatus.COMPLEET),
                repeat({ delay: 1000 }),
                takeUntil(destroySubject)
            );

            processing.subscribe({
                next: () => {
                    state.selectedFiles.forEach((file) => (file.status = FileUploadStatus.COMPLETE));
                    this.processing = false;
                    this.uploadFinished = true;
                    this.fileSelector().runValidators([], this.fileSelector().files);
                    this.uploadComplete.emit(true);
                    destroySubject.next();
                    destroySubject.complete();
                },
                error: () => {
                    this.processing = false;
                    state.selectedFiles.forEach((file) => (file.status = FileUploadStatus.FAILED));
                    this.uploadComplete.emit(false);
                    destroySubject.next();
                    destroySubject.complete();
                }
            });

            return upload;
        }

        return EMPTY;
    }

    protected isUploadFinished(opdracht: Opdracht): boolean {
        return (
            opdracht.opdracht_status === OpdrachtStatus.AFGEROND ||
            opdracht.opdracht_status === OpdrachtStatus.COMPLEET ||
            opdracht.opdracht_status === OpdrachtStatus.VERLOPEN
        );
    }

    /**
     * @param selectedFiles
     * @param originalDocument
     */
    protected isActionableDocumentState({ selectedFiles, originalDocument }: UploadDocumentState) {
        const allFilesAreNew = (files: ApUploadReadFile[]) => files.every((file) => file.status === FileUploadStatus.NEW);
        const filesContainNoErrors = (files: ApUploadReadFile[]) => files.every((file) => file.errors == null || file.errors.length === 0);
        return (
            originalDocument.upload_status === UploadStatus.AANGEMELD &&
            allFilesAreNew(selectedFiles) &&
            filesContainNoErrors(selectedFiles)
        );
    }

    /**
     * Load opdracht by id
     */
    private getOpdracht(opdrachtId: string) {
        // Reset the control
        const fileSelector = this.fileSelector();
        if (fileSelector) {
            this.control().reset();
            fileSelector.resetSelector();
        }

        this.uploadFinished = false;

        // Get the opdracht
        this.uploadbeheerResource.getOpdracht(opdrachtId).subscribe((opdracht) => {
            if (opdracht.opdracht_status) {
                this.uploadStatus.emit({ status: opdracht.opdracht_status });
            }
            if (opdracht.documenten && opdracht.documenten.length > 0) {
                const document = opdracht.documenten[0];
                document.opdracht_id = opdrachtId;
                this.document = {
                    originalDocument: document,
                    selectedFiles: []
                };

                if (this.isUploadFinished(opdracht)) {
                    this.finishedValidator.showNotification = true;
                    this.uploadFinished = true;

                    document.bestandsnamen.forEach((name) =>
                        this.document.selectedFiles.push({
                            name,
                            status: FileUploadStatus.COMPLETE
                        } as ApUploadReadFile)
                    );
                }
            }
        });
    }
}
